/* Storefront-only collection policy. Product SEO, URLs and search query text are untouched. */
(() => {
  'use strict';
  const cache = new Map();
  const ttl = 30000;
  const config = window.RFCollectionSearchConfig || {};
  const searchPath = config.searchUrl || '/search';
  const pageSize = 24;
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function sourceURL(input, page) {
    const url = new URL(input, location.origin);
    url.pathname = searchPath;
    url.hash = '';
    ['section_id', 'sections', 'preview_theme_id', 'rf_page'].forEach((name) => url.searchParams.delete(name));
    url.searchParams.set('section_id', 'rf-search-index');
    url.searchParams.set('page', String(page));
    // This changes ordering, not visibility, so temporarily sold-out items remain included.
    url.searchParams.set('options[unavailable_products]', 'last');
    return url;
  }

  async function readPage(url) {
    const response = await fetch(url, {credentials: 'same-origin'});
    if (!response.ok) throw new Error(`Search request failed (${response.status})`);
    const doc = parse(await response.text());
    const root = doc.querySelector('[data-rf-search-index]');
    if (!root) throw new Error('Search rendering endpoint is unavailable');
    const pages = Number(root.dataset.pages);
    if (!Number.isInteger(pages) || pages < 0 || pages > 100) throw new Error('Search page range is invalid');
    const entries = [...root.querySelectorAll('[data-rf-search-entry]')].map((entry) => ({
      key: entry.dataset.key,
      type: entry.dataset.type,
      title: entry.dataset.title,
      url: entry.dataset.url,
      card: entry.querySelector('[data-rf-search-card]')?.innerHTML || '',
      suggestion: entry.querySelector('[data-rf-search-suggestion]')?.innerHTML || ''
    }));
    return {pages, entries};
  }

  async function load(input) {
    const firstURL = sourceURL(input, 1);
    const key = firstURL.href;
    const cached = cache.get(key);
    if (cached && Date.now() - cached.time < ttl) return cached.promise;
    const promise = (async () => {
      const first = await readPage(firstURL);
      const pages = new Array(Math.max(first.pages, 1));
      pages[0] = first;
      let next = 2;
      const worker = async () => {
        while (next <= first.pages) {
          const number = next++;
          pages[number - 1] = await readPage(sourceURL(input, number));
        }
      };
      await Promise.all(Array.from({length: Math.min(3, Math.max(0, first.pages - 1))}, worker));
      const seen = new Set();
      return pages.flatMap((page) => page.entries).filter((entry) => {
        if (seen.has(entry.key)) return false;
        seen.add(entry.key);
        return true;
      });
    })();
    cache.set(key, {time: Date.now(), promise});
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    try { return await promise; }
    catch (error) { cache.delete(key); throw error; }
  }

  const escapeHTML = (text) => String(text).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[char]));
  let generation = 0;
  let activeEntries = [];
  let activeQuery = '';

  function updateCounts(count) {
    document.querySelectorAll('#ProductCount,#ProductCountDesktop').forEach((el) => {
      el.textContent = `${count} ${count === 1 ? 'result' : 'results'}`;
    });
    // Native per-facet counts include excluded items: omit them rather than show misleading totals.
    document.querySelectorAll('.rf-search .facet-checkbox__text').forEach((el) => {
      const label = el.querySelector('.facet-checkbox__text-label');
      if (label) el.replaceChildren(label);
    });
    document.querySelectorAll('.rf-search label .visually-hidden').forEach((el) => {
      if (/\(\s*\d+\s+(?:product|products)\s*\)/i.test(el.textContent)) {
        el.textContent = el.textContent.replace(/\s*\(\s*\d+\s+(?:product|products)\s*\)/gi, '');
      }
    });
  }

  function pageLink(page, label, current) {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    if (page === 1) url.searchParams.delete('rf_page');
    else url.searchParams.set('rf_page', String(page));
    return `<${current ? 'span' : 'a'}${current ? ' aria-current="page"' :
      ` href="${escapeHTML(url.pathname + url.search)}" data-rf-page="${page}"`}>${escapeHTML(label)}</${current ? 'span' : 'a'}>`;
  }

  function render(entries, requestedPage, scroll) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const oldGrid = container.querySelector('#product-grid');
    const sectionId = oldGrid?.dataset.id || container.dataset.rfSectionId || '';
    container.dataset.rfSectionId = sectionId;
    const pages = Math.max(1, Math.ceil(entries.length / pageSize));
    const page = Math.max(1, Math.min(pages, requestedPage || 1));
    const rows = entries.slice((page - 1) * pageSize, page * pageSize);
    const query = new URL(location.href).searchParams.get('q') || '';
    let content;
    if (!entries.length) {
      content = `<div class="rf-search-policy-empty"><h2>No matching products or pages</h2><p>No current results matched “${escapeHTML(query)}”. Try a different keyword or adjust your filters.</p></div>`;
    } else {
      content = `<ul class="grid product-grid grid--2-col-tablet-down grid--4-col-desktop" role="list">${rows.map((row) => row.card).join('')}</ul>`;
    }
    let pagination = '';
    if (pages > 1) {
      if (page > 1) pagination += pageLink(page - 1, 'Previous', false);
      const numbers = [...new Set([1, page - 1, page, page + 1, pages].filter((n) => n >= 1 && n <= pages))].sort((a, b) => a - b);
      let previous = 0;
      numbers.forEach((number) => {
        if (previous && number > previous + 1) pagination += '<span aria-hidden="true">…</span>';
        pagination += pageLink(number, String(number), number === page);
        previous = number;
      });
      if (page < pages) pagination += pageLink(page + 1, 'Next', false);
      pagination = `<nav class="rf-search-policy-pagination" aria-label="Search result pages">${pagination}</nav>`;
    }
    const wrapper = document.createElement('div');
    wrapper.className = oldGrid?.className || 'template-search__results collection page-width';
    wrapper.id = 'product-grid';
    wrapper.dataset.id = sectionId;
    wrapper.dataset.rfFilteredView = 'true';
    wrapper.innerHTML = content + pagination;
    container.replaceChildren(wrapper);
    container.removeAttribute('data-rf-search-pending');
    container.setAttribute('aria-busy', 'false');
    updateCounts(entries.length);
    document.querySelectorAll('.rf-search-hero p[role="status"]').forEach((el) => { el.hidden = true; });
    document.querySelectorAll('.rf-search-help').forEach((el) => { el.hidden = entries.length > 0; });
    if (entries.length) document.querySelector('.template-search')?.classList.remove('template-search--empty');
    if (!document.querySelector('#ProductCount,#ProductCountDesktop')) {
      const count = document.createElement('p');
      count.className = 'rf-search-policy-count';
      count.setAttribute('role', 'status');
      count.textContent = `${entries.length} ${entries.length === 1 ? 'result' : 'results'}`;
      wrapper.prepend(count);
    }
    document.title = `Search: ${entries.length} results for "${query}" – ${config.shopName || 'InTheHoleGolf'}`;
    const currentURL = new URL(location.href);
    const currentPage = Number(currentURL.searchParams.get('rf_page') || 1);
    if (currentPage !== page || currentURL.searchParams.has('page')) {
      currentURL.searchParams.delete('page');
      if (page === 1) currentURL.searchParams.delete('rf_page');
      else currentURL.searchParams.set('rf_page', String(page));
      history.replaceState(history.state, '', currentURL.pathname + currentURL.search);
    }
    if (scroll) container.scrollIntoView({block: 'start', behavior: 'auto'});
  }

  async function refresh() {
    if (!document.body.classList.contains('rf-search')) return;
    const params = new URL(location.href).searchParams;
    if (!params.get('q')?.trim()) return;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const ticket = ++generation;
    container.dataset.rfSearchPending = 'true';
    container.setAttribute('aria-busy', 'true');
    container.querySelector('.rf-search-policy-status')?.remove();
    const status = document.createElement('div');
    status.className = 'rf-search-policy-status';
    status.setAttribute('role', 'status');
    status.textContent = 'Searching current products…';
    container.prepend(status);
    try {
      const entries = await load(location.href);
      if (ticket !== generation) return;
      activeEntries = entries;
      const keyURL = new URL(location.href);
      keyURL.searchParams.delete('page');
      keyURL.searchParams.delete('rf_page');
      activeQuery = keyURL.search;
      render(entries, Number(params.get('rf_page') || params.get('page') || 1), false);
    } catch (error) {
      if (ticket !== generation) return;
      container.removeAttribute('data-rf-search-pending');
      container.setAttribute('aria-busy', 'false');
      status.textContent = 'Search could not finish loading. Discontinued products remain hidden.';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Try again';
      retry.addEventListener('click', refresh);
      status.append(retry);
      updateCounts('—');
      container.querySelectorAll('.pagination-wrapper').forEach((el) => { el.hidden = true; });
      console.warn('Collection-aware search:', error.message);
    }
  }

  async function filterPredictions(markup, searchTerm) {
    const doc = parse(markup);
    let list = doc.getElementById('predictive-search-results-products-list');
    const nativeCount = list?.children.length || 0;
    if (nativeCount < 5) {
      const url = new URL(searchPath, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');
      try {
        const products = (await load(url.href)).filter((entry) => entry.type === 'product').slice(0, 10);
        if (products.length) {
          if (!list) {
            const group = doc.createElement('div');
            group.className = 'predictive-search__result-group';
            group.innerHTML = '<div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2><ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul></div>';
            const wrapper = doc.getElementById('predictive-search-results-groups-wrapper');
            wrapper?.append(group);
            list = group.querySelector('ul');
          }
          list.innerHTML = products.map((entry) => entry.suggestion).join('');
          [...list.children].forEach((li, index) => { li.id = `predictive-search-option-product-${index + 1}`; });
        }
      } catch (error) {
        // Never fall back to excluded products if the enrichment request fails.
        console.warn('Collection-aware suggestions:', error.message);
      }
    }
    const count = list?.children.length || 0;
    if (list) list.parentElement.hidden = count === 0;
    const live = doc.querySelector('[data-predictive-search-live-region-count-value]');
    if (live) live.textContent = `${count} ${count === 1 ? 'product' : 'products'}. Other suggestions may be available.`;
    return doc.body.innerHTML;
  }

  window.RFCollectionSearch = {load, filterPredictions, refresh};
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-rf-page]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    history.pushState(history.state, '', link.href);
    render(activeEntries, Number(link.dataset.rfPage), true);
  });
  addEventListener('popstate', () => {
    if (!document.body.classList.contains('rf-search')) return;
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('rf_page');
    if (url.search === activeQuery) render(activeEntries, Number(new URL(location.href).searchParams.get('rf_page') || new URL(location.href).searchParams.get('page') || 1), false);
    else refresh();
  });
  document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('ProductGridContainer');
    if (container && document.body.classList.contains('rf-search')) {
      const observer = new MutationObserver(() => {
        if (container.querySelector('[data-rf-filtered-view]')) return;
        if (container.hasAttribute('data-rf-search-pending') && container.querySelector('.rf-search-policy-status')) return;
        refresh();
      });
      observer.observe(container, {childList: true});
      refresh();
    }
  });
})();
